import {
  CommentDTO,
  CreateWorkTaskDTO2,
  CustomerAppointmentDTO,
  DayTaskDTO,
  EmailNotificationTypeEnum,
  FileAnswerUploadDTO,
  MassCreationRequestDTO2,
  MassCreationStretchRequestDTO,
  PersonDTO,
  RevisitDTO,
  TaskListRequestDTO,
  UpdateWorkTaskDTO2,
  WorkPlanItemStatusDTO,
  WorkTaskDepartmentDTO2,
  WorkTaskDTO2,
  WorkTaskProjectDTO,
  WorkTaskStatus,
  WorkTaskStatusDTO
} from '../api/api';
import WorkClient from '../api/workClient';
import { formatDateStringISO } from '../utils/dateHandling';

const getWorkTaskList = (body: TaskListRequestDTO, abortController?: AbortController) => {
  return WorkClient.taskListPOST(body, abortController?.signal);
};

const getTaskListInfo = (departmentId?: number) => {
  return WorkClient.taskListGET(departmentId);
};

const getTask = async (id: number) => {
  return WorkClient.getBaseTask2(id);
};

const assignToMe = async (id: number) => {
  return WorkClient.assignMeAsResponsible(id);
}

const cancelTasks = (taskIds: number[]) => {
  return WorkClient.batchCancelWorkTasks(taskIds);
};

const getWorkPlan = () => {
  return WorkClient.getWorkPlan5();
};

const getWorkPlanWithBookings = () => {
  return WorkClient.getWorkPlan6();
};

const setTaskStatus = (tasks: WorkTaskStatusDTO[]) => {
  return WorkClient.setTaskStatus(tasks);
};

const setWorkPlanTaskStatus = (task: WorkPlanItemStatusDTO) => {
  return WorkClient.setWorkPlanTaskStatus(task);
};

const getReasonCodes = (taskTypeId: number, status: WorkTaskStatus) => {
  return WorkClient.getReasonCodes(taskTypeId, status);
};

const suspendTask = (body: DayTaskDTO) => {
  return WorkClient.completeDayOfMultiDayTask(body);
};

const updateWorkTask = (taskId: number, fields: UpdateWorkTaskDTO2) => {
  return WorkClient.updateWorkTask2(taskId, fields);
};

const createTask = (workTask: CreateWorkTaskDTO2): Promise<WorkTaskDTO2> => {
  return WorkClient.createWorkTask2(workTask);
};

const getAlarmTypes = () => {
  return WorkClient.getAlarmTypes();
};

const updateDepartments = (departments: WorkTaskDepartmentDTO2[]) => {
  return WorkClient.setTaskDepartment2(departments);
};

const getNearbyTask = (lat: number, lng: number, assetId: string) => {
  return WorkClient.nearby3(lat, lng, assetId);
};

const postComment = (comment: CommentDTO) => {
  return WorkClient.comments(comment);
};

const flagComment = (commentId: number, workTaskId: number, flag: boolean) => {
  return WorkClient.flagComment(commentId, workTaskId, flag);
};

const assignToCaseWorker = (email?: string, name?: string, taskIds?: number[]) => {
  return WorkClient.assignTasksToCaseWorker(email, name, taskIds);
};

const getPdf = (taskId: number, contractorAgreementId: number) => {
  return WorkClient.getPDFV2(taskId, contractorAgreementId);
};

const customerAppointment = (taskId: number, body: CustomerAppointmentDTO) => {
  return WorkClient.customerAppointment(taskId, body);
};

const getTechnicianCalendarTasks = (from: string, to: string) => {
  return WorkClient.getTechnicianCalendarTasks(from, to);
};

const getTechnicianCalendarBookings = (from: string, to: string) => {
  return WorkClient.getTechnicianCalendarBookings(from, to);
};

const getProjectById = (projectNumber: string) => {
  return WorkClient.getProjectById(projectNumber);
};

const updateProjectNumber = (workTaskId: number, body: WorkTaskProjectDTO | undefined) => {
  return WorkClient.updateProjectNumberForWorkTask(workTaskId, body);
};

const massCreateWorkTasks = (body: MassCreationRequestDTO2) => {
  return WorkClient.massCreateWorkTasks2(body);
};

const massCreateStretchWorkTasks = (body: MassCreationStretchRequestDTO) => {
  return WorkClient.massCreateStretchWorkTasks(body);
};

const addFileAnswers = (body: FileAnswerUploadDTO) => {
  return WorkClient.addFileAnswers(body);
};

const getThumbNail = (taskId: number, fileName: string, answerId: number) => {
  return WorkClient.thumbnail2(taskId, fileName, answerId);
};

const updateWorkTaskTag = (tagId: number | undefined, ids: number[]) => {
  return WorkClient.setWorkTasksTag(tagId, ids);
};

const sendToPlanning = (workTaskIds: number[]) => {
  return WorkClient.sendWorkTasksToPlanning(workTaskIds);
};

const planWithD365 = (workTaskIds: number[]) => {
  return WorkClient.planWithD365(workTaskIds);
}

const getSchedule = (date: string) => {
  return WorkClient.getPlanningEvents(formatDateStringISO(date));
};

const subscribeToEmailNotifications = (
  workTaskId: number,
  notificationType: EmailNotificationTypeEnum,
  subscribe: boolean
) => {
  return WorkClient.emailNotification(workTaskId, notificationType, subscribe);
};

const isSubscribingToWorkTask = (workTaskId: number, notificationType: EmailNotificationTypeEnum) => {
  return WorkClient.isSubscribed(workTaskId, notificationType);
};

const getRevisit = (workTaskId: number) => {
  return WorkClient.getRevisit(workTaskId);
};

const createRevisit = (body: RevisitDTO) => {
  return WorkClient.createRevisit(body);
};


const updateRevisit = (body: RevisitDTO) => {
  return WorkClient.updateRevisit(body);
};

const deleteRevisit = (workTaskId: number) => {
  return WorkClient.deleteRevisit(workTaskId);
};

const driveToBooking = (bookingId: number) => {
  return WorkClient.driveToBooking(bookingId);
}

const leaveBooking = (bookingId: number) => {
  return WorkClient.leaveBooking(bookingId);
}

const startBooking = (bookingId: number) => {
  return WorkClient.startBooking(bookingId);
}

const closeBookingDialog = (bookingId: number) => {
  return WorkClient.closeBookingDialog(bookingId);
}

const getCloseTaskDialogue = (workTaskId: number) => {
  return WorkClient.getCloseTaskDialogue(workTaskId);
}

const getBookedTechnicians = (workTaskId: number) => {
  return WorkClient.getBookedTechnicians(workTaskId);
}

const transferResponsibility = (workTaskId: number, newTechnician: PersonDTO) => {
  return WorkClient.transferResponsibility(workTaskId, newTechnician);
}

const TaskService = {
  getTaskListInfo,
  getWorkTaskList,
  getTask,
  getWorkPlan,
  getWorkPlanWithBookings,
  assignToMe,
  createTask,
  getAlarmTypes,
  getNearbyTask,
  setTaskStatus,
  setWorkPlanTaskStatus,
  getReasonCodes,
  suspendTask,
  updateDepartments,
  postComment,
  assignToCaseWorker,
  getPdf,
  updateWorkTask,
  customerAppointment,
  getTechnicianCalendarTasks,
  getTechnicianCalendarBookings,
  getProjectById,
  updateProjectNumber,
  massCreateWorkTasks,
  addFileAnswers,
  getThumbNail,
  updateWorkTaskTag,
  sendToPlanning,
  getSchedule,
  massCreateStretchWorkTasks,
  flagComment,
  cancelTasks,
  subscribeToEmailNotifications,
  isSubscribingToWorkTask,
  getRevisit,
  createRevisit,
  updateRevisit,
  deleteRevisit,
  driveToBooking,
  leaveBooking,
  startBooking,
  closeBookingDialog,
  getCloseTaskDialogue,
  getBookedTechnicians,
  transferResponsibility,
  planWithD365,
};

export default TaskService;
